import React from "react";
import { Link } from "gatsby";

import {
  paragraphTextSmall,
  heading100,
  heading150,
  heading200,
  heading300,
  paragraphTextMedium,
} from "../../../stitches/combinedStyles";
import { styled } from "../../../stitches/stitches.config";
import Button from "../Button/Button";
import TalkToAnExpert from "./talk.inline.svg";
import BookAFreeTrial from "./book.inline.svg";
import RequestAQuote from "./request.inline.svg";
import TalkToAnExpertWhite from "./talk.white.inline.svg";
import BookAFreeTrialWhite from "./book.white.inline.svg";
import RequestAQuoteWhite from "./request.white.inline.svg";
import NextStepsOvalTop from "./nextStepsOvalTop.png";
import NextStepsOvalBottom from "./nextStepsOvalBottom.png";

const title = `Next steps`;

const NextSteps = ({showTitle=true}) => {
  const blocks = [
    {
      icon: <TalkToAnExpert />,
      iconMobile: <TalkToAnExpertWhite />,
      title: "Talk to an expert",
      description:
        "Get in touch with one of our friendly materials scientists",
      secondaryButton: {
        text: "Ask a question",
        path: "/ask-a-question",
      },
    },
    {
      icon: <BookAFreeTrial />,
      iconMobile: <BookAFreeTrialWhite />,
      title: "Book a trial",
      description:
        "We’ll carry out PIP testing on your samples",
      secondaryButton: {
        text: "Book a trial",
        path: "https://plastometrex-6034553.hs-sites.com/-df211dc3-e9d6-4251-8c4d-43afb8cf9f77",
      },
    },
    {
      icon: <RequestAQuote />,
      iconMobile: <RequestAQuoteWhite />,
      title: "Request a quote",
      description:
        "Talk to our sales team to receive a custom quotation ",
      secondaryButton: {
        text: "Request a quote",
        path: "/request-a-quote",
      },
    }
  ];

  const Block = ({
    icon,
    iconMobile,
    title,
    description,
    linkPath,
    text,
  }: {
    icon: any;
    iconMobile: any;
    title: string;
    description: string;
    linkPath: string;
    text: string
  }) => (
    <BlockWrapper>
      <Row>
        <StyledSvg>
          <DesktopContainer>{icon}</DesktopContainer>
          <MobileContainer>{iconMobile}</MobileContainer>
        </StyledSvg>
        <MobileContainer>
          <Link to={linkPath}>
            <Button size="large" tier="bordered" arrow>
              {title}
            </Button>
          </Link>
        </MobileContainer>
      </Row>
      <DesktopContainer>
        <BlockTitle>{title}</BlockTitle>
        <BlockDescription>{description}</BlockDescription>
        
      </DesktopContainer>
      <DesktopContainer className="bottom">
      <Link to={linkPath}>
          <Button size="large" tier="primary" arrow fullWidth>
            {text}
          </Button>
        </Link>
      </DesktopContainer>
      
    </BlockWrapper>
  );

  return (
    <Container>
      <ContentContainer>
        <TextContent>
          {showTitle && <Title>{title}</Title>}
        </TextContent>
        <BlockContent>
          {blocks.map((block, i) => (
            <Block
              icon={block.icon}
              iconMobile={block.iconMobile}
              title={block.title}
              description={block.description}
              text={block.secondaryButton.text}
              linkPath={block.secondaryButton.path}
              key={block.secondaryButton.path}
            />
          ))}
        </BlockContent>
      </ContentContainer>
    </Container>
  );
};

export default NextSteps;



const Container = styled("div", {
  backgroundColor: "$blue100",
  borderRadius: "10px",
  width: "90%",
  maxWidth: "1200px",
  margin: "0 auto",
  paddingTop: 16,
  backgroundImage: `url(${NextStepsOvalTop}),url(${NextStepsOvalBottom})`,
  backgroundPosition: "left top, right 250px",
  backgroundRepeat: "no-repeat,no-repeat",
  backgroundSize: "100%,100%",
  ".bottom": {
    marginTop:"auto",
    },
  "@md": {
    backgroundColor: "$transparent",
    backgroundImage: `none`,
    backgroundPosition: "left top",
    backgroundRepeat: "no-repeat",
    backgroundSize: "auto 100%",
  }
});

const TextContent = styled("div", {
  maxWidth: 600,
  marginLeft: "auto",
  marginRight: "auto",
  "@lg": {
    flexBasis: "30%",
  },
});

const Title = styled("h4", {
  ...heading100,
  marginBottom: 16,
  color: "$white",
  marginTop: 16,
  "@md": {
    ...heading300,
    letterSpacing: -0.76,
    color: "$black",
    marginTop: 64,
    marginBottom: 58,
  },
});

const ContentContainer = styled("div", {
  textAlign: "center",
  marginBottom: "64px",
});

const BlockContent = styled("div", {
  display: "flex",
  flexDirection: "column",
  padding: "8px 0",
  "@md": {
    flexDirection: "row",
    justifyContent: "space-between",
    padding: "10px 0",
  },
})

const BlockWrapper = styled("div", {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textAlign: "center",
  maxWidth: 600,
  marginLeft: "auto",
  marginRight: "auto",
  backgroundColor: "$transparent",
  "@md": {
    flexBasis: "30%",
    backgroundColor: "$white",
    border: "1px solid rgba(26, 26, 26, 0.1)",
    borderRadius: 15,
    padding: "50px 2%",
    marginBottom: 16,
    "&:hover": {
      backgroundColor: "$white",
      border: "1px solid rgba(26, 26, 26, 0.1)",
      boxShadow: "5px 4px 6px rgba(0, 0, 0, 0.08)",
    },
  },
});

const BlockTitle = styled("h6", {
  ...heading200,
  margin: "26px 0 12px 0",
});

const BlockDescription = styled("p", {
  ...paragraphTextSmall,
  margin: 0,
  marginBottom: 26,
});

const DesktopContainer = styled("div", {
  display: "none",
  "@md": {
    display: "block",
  },
})

const MobileContainer = styled("div", {
  display: "block",
  "@md": {
    display: "none",
  },
})

const Row = styled("div", {
  display: "grid",
  gridTemplateColumns: "1fr auto",
  marginBottom: 20,
  button: {
    fontSize: 16,
    padding: "8px 16px",
    minWidth: 190,
    filter: "brightness(100)",
  },
  "@md": {
    filter: "none",
    svg: { width: 80 },
  },
})

const StyledSvg = styled("div", {
  svg: {
    width: 44,
    height: 44,
    marginRight: 10,
  },
  "@md": {
    svg: {
      width: 80,
      height: 80,
    },
  },
})